import { Component, OnInit, OnDestroy } from '@angular/core';
import { ExportedFoldersService, ExportedFolderOutModel, ExportedFolderOrderOutModel } from 'src/app/services/exported-folders/exported-folders.service';
import { OrderOutModel } from 'src/app/models/order-out-model/order-out-model';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-exported-folders',
  templateUrl: './exported-folders.component.html',
  styleUrls: ['./exported-folders.component.css']
})
export class ExportedFoldersComponent implements OnInit, OnDestroy {
  folders: ExportedFolderOutModel[] = [];
  selectedFolder: ExportedFolderOutModel | null = null;
  selectedFolderOrders: ExportedFolderOrderOutModel[] = [];
  allFolderOrders: ExportedFolderOrderOutModel[] = []; // Todas las órdenes de la carpeta (sin filtrar)
  
  // Orden expandida para ver detalles
  expandedOrder: ExportedFolderOrderOutModel | null = null;
  
  // Repuestos expandidos para ver detalles (usando un Set para tracking)
  expandedSpareParts = new Set<string>();
  
  // Estado de carga
  isLoading = false;
  isLoadingOrders = false;
  
  // Búsqueda y filtros
  searchTerm = '';
  folderSearchTerm = '';
  dateFilter = 'all';
  
  // Lista filtrada de carpetas para mostrar
  filteredFolders: ExportedFolderOutModel[] = [];
  
  // Paginación de tabla de órdenes
  currentPage = 1;
  pageSize = 50;
  totalOrders = 0;
  totalPages = 1;
  
  // Subject para debounce de búsqueda
  private searchSubject = new Subject<string>();
  
  constructor(
    private exportedFoldersService: ExportedFoldersService
  ) {
    // La búsqueda se activará manualmente con Enter o botón
  }

  ngOnInit() {
    this.loadFolders();
  }

  ngOnDestroy() {
    this.searchSubject.complete();
  }

  async loadFolders() {
    try {
      this.isLoading = true;
      // Usar el endpoint optimizado que solo trae metadatos
      const folders = await this.exportedFoldersService.getExportedFolders(0, false, false).toPromise();
      this.folders = folders || [];
      this.filteredFolders = [...this.folders]; // Inicializar lista filtrada
      this.applyFolderFilters();
    } catch (error) {
      console.error('Error al cargar carpetas:', error);
      this.showError('Error al cargar las carpetas exportadas');
    } finally {
      this.isLoading = false;
    }
  }

  applyFolderFilters() {
    let filtered = [...this.folders];

    // Aplicar búsqueda por nombre
    if (this.folderSearchTerm) {
      filtered = filtered.filter(folder => 
        folder.folderName.toLowerCase().includes(this.folderSearchTerm.toLowerCase()) ||
        folder.description.toLowerCase().includes(this.folderSearchTerm.toLowerCase())
      );
    }

    // Aplicar filtro de fecha
    if (this.dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const week = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      const month = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

      filtered = filtered.filter(folder => {
        const folderDate = new Date(folder.exportDate);
        switch (this.dateFilter) {
          case 'today':
            return folderDate >= today;
          case 'week':
            return folderDate >= week;
          case 'month':
            return folderDate >= month;
          default:
            return true;
        }
      });
    }

    // Actualizar la lista filtrada
    this.filteredFolders = filtered;
  }

  // Método para buscar carpetas
  searchFolders() {
    this.applyFolderFilters();
  }

  // Método para limpiar búsqueda de carpetas
  clearFolderSearch() {
    this.folderSearchTerm = '';
    this.applyFolderFilters();
  }

  // Método para manejar cambios en el filtro de fecha
  onDateFilterChange() {
    this.applyFolderFilters();
  }

  async viewOrders(folder: ExportedFolderOutModel) {
    try {
      this.selectedFolder = folder;
      this.isLoadingOrders = true;
      this.searchTerm = '';
      this.expandedOrder = null; // Cerrar cualquier orden expandida
      this.expandedSpareParts.clear(); // Limpiar expansiones de repuestos
      this.currentPage = 1; // Resetear paginación

      if (this.isMetadataOnlyExport(folder)) {
        this.selectedFolderOrders = [];
        this.totalOrders = 0;
        this.totalPages = 1;
        return;
      }
      
      // Cargar primera página de órdenes con paginación
      const response = await this.exportedFoldersService.getFolderOrders(folder.folderId, '', 1, this.pageSize).toPromise();
      this.selectedFolderOrders = response?.orders || [];
      this.totalOrders = response?.total || 0;
      this.totalPages = response?.totalPages || 1;
      this.currentPage = 1;
    } catch (error) {
      console.error('Error al cargar órdenes de la carpeta:', error);
      this.showError('Error al cargar las órdenes de la carpeta');
    } finally {
      this.isLoadingOrders = false;
    }
  }

  // Método público para la búsqueda inmediata (usado por el botón)
  searchInFolder() {
    this.performSearch(this.searchTerm);
  }

  // Método privado que realiza la búsqueda real (con paginación)
  private async performSearch(searchTerm: string) {
    if (!this.selectedFolder) return;

    try {
      this.isLoadingOrders = true;
      this.expandedOrder = null; // Cerrar orden expandida al buscar
      this.expandedSpareParts.clear(); // Limpiar expansiones de repuestos
      this.currentPage = 1; // Resetear paginación
      
      // Realizar búsqueda con paginación en el servidor
      const response = await this.exportedFoldersService.getFolderOrders(
        this.selectedFolder.folderId, 
        searchTerm, 
        1, 
        this.pageSize
      ).toPromise();
      
      this.selectedFolderOrders = response?.orders || [];
      this.totalOrders = response?.total || 0;
      this.totalPages = response?.totalPages || 1;
      this.currentPage = 1;
    } catch (error) {
      console.error('Error al buscar órdenes:', error);
      this.showError('Error al buscar las órdenes');
    } finally {
      this.isLoadingOrders = false;
    }
  }

  // Método para manejar cambios en el input de búsqueda
  onSearchInputChange() {
    // No hacer nada automáticamente, solo cuando se presione Enter o se haga clic en buscar
  }


  // Método para cargar una página específica
  async loadPage(page: number) {
    if (!this.selectedFolder || page < 1 || page > this.totalPages) return;

    try {
      this.isLoadingOrders = true;
      this.currentPage = page;
      
      const response = await this.exportedFoldersService.getFolderOrders(
        this.selectedFolder.folderId,
        this.searchTerm,
        page,
        this.pageSize
      ).toPromise();
      
      this.selectedFolderOrders = response?.orders || [];
      this.totalOrders = response?.total || 0;
      this.totalPages = response?.totalPages || 1;
      
      // Cerrar expansiones al cambiar de página
      this.expandedOrder = null;
      this.expandedSpareParts.clear();
      
    } catch (error) {
      console.error('Error al cargar página:', error);
      this.showError('Error al cargar la página');
    } finally {
      this.isLoadingOrders = false;
    }
  }

  // Método para expandir/contraer una orden
  toggleOrderExpansion(order: ExportedFolderOrderOutModel) {
    if (this.expandedOrder?.id === order.id) {
      // Si ya está expandida, la contraemos
      this.expandedOrder = null;
      // Limpiar expansiones de repuestos de esta orden
      this.clearSparePartExpansions(order.orderSnapshot?.orderId || order.orderId);
    } else {
      // Cerrar orden anterior si existe
      if (this.expandedOrder) {
        this.clearSparePartExpansions(this.expandedOrder.orderSnapshot?.orderId || this.expandedOrder.orderId);
      }
      // Expandir la orden seleccionada
      this.expandedOrder = order;
    }
  }

  // Verificar si una orden está expandida
  isOrderExpanded(order: ExportedFolderOrderOutModel): boolean {
    return this.expandedOrder?.id === order.id;
  }

  // Obtener los repuestos de una orden desde el snapshot
  getOrderSpareParts(order: ExportedFolderOrderOutModel): any[] {
    try {
      if (order.orderSnapshot && order.orderSnapshot.spareParts) {
        return order.orderSnapshot.spareParts;
      }
      return [];
    } catch (error) {
      console.error('Error al obtener repuestos de la orden:', error);
      return [];
    }
  }

  // Calcular el total de una orden
  getOrderTotalFromSnapshot(order: ExportedFolderOrderOutModel): number {
    try {
      const spareParts = this.getOrderSpareParts(order);
      return spareParts.reduce((total, part) => {
        return total + (part.amount * part.unitPrice);
      }, 0);
    } catch (error) {
      console.error('Error al calcular total de la orden:', error);
      return 0;
    }
  }

  // Obtener la clase CSS para el estado de un repuesto
  getSparePartStatusClass(status: string): string {
    const statusClasses: { [key: string]: string } = {
      'CREADO': 'bg-blue-100 text-blue-800',
      'ATRASADO': 'bg-red-100 text-red-800',
      'DEVOLUCION-PROVEEDOR': 'bg-orange-100 text-orange-800',
      'STOCK-ENTREGA-EN-LOCAL': 'bg-yellow-100 text-yellow-800',
      'STOCK-ENTREGA-AL-CLIENTE': 'bg-yellow-100 text-yellow-800',
      'STOCK-OTRAS-EXPORTACIONES': 'bg-purple-100 text-purple-800',
      'STOCK-EXPORTACION-ACTUAL': 'bg-purple-100 text-purple-800',
      'EMPAQUETADO-EXPORTACION-ACTUAL': 'bg-indigo-100 text-indigo-800',
      'EXPORTADO': 'bg-green-100 text-green-800',
      'ENTREGADO-EN-LOCAL': 'bg-green-100 text-green-800',
      'ENVIADO-AL-TALLER': 'bg-gray-100 text-gray-800'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-800';
  }

  closeModal() {
    this.selectedFolder = null;
    this.selectedFolderOrders = [];
    this.allFolderOrders = []; // Limpiar órdenes originales
    this.expandedOrder = null;
    this.expandedSpareParts.clear(); // Limpiar expansiones de repuestos
    this.searchTerm = '';
    this.currentPage = 1;
    this.totalOrders = 0;
    this.totalPages = 1;
  }



  async deleteFolder(folder: ExportedFolderOutModel) {
    if (confirm(`¿Estás seguro de eliminar la carpeta "${folder.folderName}"? Esta acción no se puede deshacer.`)) {
      try {
        await this.exportedFoldersService.deleteExportedFolder(folder.folderId).toPromise();
        this.showSuccess(`Carpeta "${folder.folderName}" eliminada exitosamente`);
        this.loadFolders(); // Recargar la lista
      } catch (error) {
        console.error('Error al eliminar carpeta:', error);
        this.showError('Error al eliminar la carpeta');
      }
    }
  }

  // Métodos auxiliares para la tabla de órdenes
  getDisplayedOrders(): ExportedFolderOrderOutModel[] {
    // Ya no necesitamos calcular el slice porque el servidor nos da la página correcta
    return this.selectedFolderOrders;
  }

  getTotalPages(): number {
    return this.totalPages;
  }

  async goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      await this.loadPage(page);
    }
  }

  // Método para generar números de página con elipsis
  getPageNumbers(): number[] {
    const pages: number[] = [];
    const totalPages = this.totalPages;
    const currentPage = this.currentPage;
    
    if (totalPages <= 7) {
      // Si hay 7 páginas o menos, mostrar todas
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Si hay más de 7 páginas, mostrar con elipsis
      if (currentPage <= 4) {
        // Páginas iniciales
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push(-1); // Elipsis
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        // Páginas finales
        pages.push(1);
        pages.push(-1); // Elipsis
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Páginas intermedias
        pages.push(1);
        pages.push(-1); // Elipsis
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push(-1); // Elipsis
        pages.push(totalPages);
      }
    }
    
    return pages;
  }

  getOrderTotal(order: OrderOutModel): number {
    if (!order || !order.spareParts) return 0;
    return order.spareParts.reduce((total, part) => {
      return total + (part.amount * part.unitPrice);
    }, 0);
  }

  formatDate(date: Date | string): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('es-ES');
  }

  formatDateTime(date: Date | string): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('es-ES');
  }

  getExportedSparePartCount(folder: ExportedFolderOutModel): number | null {
    const criteria = this.getFilterCriteriaObject(folder);
    if (!criteria) return null;

    if (criteria.sparePartCount != null) {
      const parsed = Number(criteria.sparePartCount);
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  }

  isSparePartsOnlyExport(folder: ExportedFolderOutModel | null): boolean {
    if (!folder) return false;
    const name = (folder.folderName || '').toLowerCase();
    const description = (folder.description || '').toLowerCase();
    return name.startsWith('excelspareparts_') || description.includes('exportación de repuestos');
  }

  isMetadataOnlyExport(folder: ExportedFolderOutModel | null): boolean {
    if (!folder) return false;
    const criteria = this.getFilterCriteriaObject(folder);
    return criteria?.mode === 'basic-record';
  }

  getExportTypeLabel(folder: ExportedFolderOutModel | null): string {
    if (!folder) return 'N/D';
    if (!this.isMetadataOnlyExport(folder)) return 'Snapshot completo';
    return this.isSparePartsOnlyExport(folder) ? 'Excel de repuestos' : 'Excel de órdenes';
  }

  getExportedItemUnit(folder: ExportedFolderOutModel | null): string {
    if (!this.isMetadataOnlyExport(folder)) return 'repuestos';
    return this.isSparePartsOnlyExport(folder) ? 'repuestos' : 'filas';
  }

  getExportFileName(folder: ExportedFolderOutModel | null): string {
    return this.getFilterCriteriaObject(folder)?.fileName || 'N/D';
  }

  getMetadataExportedAt(folder: ExportedFolderOutModel | null): string {
    if (!folder) return 'N/A';
    const exportedAt = this.getFilterCriteriaObject(folder)?.exportedAt;
    return exportedAt ? this.formatDateTime(exportedAt) : this.formatDateTime(folder.exportDate);
  }

  private getFilterCriteriaObject(folder: ExportedFolderOutModel | null): any | null {
    const criteria = folder?.filterCriteria;
    if (!criteria) return null;

    if (typeof criteria === 'object') {
      return criteria;
    }

    if (typeof criteria === 'string') {
      try {
        return JSON.parse(criteria);
      } catch {
        return null;
      }
    }

    return null;
  }

  getStatusClass(status: string): string {
    const statusClasses: { [key: string]: string } = {
      'CREADO': 'bg-blue-100 text-blue-800',
      'LLEGO': 'bg-orange-100 text-orange-800',
      'EN PROCESO': 'bg-orange-100 text-orange-800',
      'FINALIZADO': 'bg-green-100 text-green-800',
      'CANCELADO': 'bg-red-100 text-red-800',
      'ATENCION': 'bg-red-100 text-red-800',
      'PENDIENTE': 'bg-purple-100 text-purple-800',
      'REPUESTOS-PENDIENTES': 'bg-purple-100 text-purple-800'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-800';
  }

  private showSuccess(message: string) {
    alert(message); // Temporal
  }

  private showError(message: string) {
    console.error('ERROR:', message);
    alert(message); // Temporal
  }

  // Métodos para expansión de repuestos
  toggleSparePartExpansion(orderId: number, sparePartId: number, event?: Event) {
    if (event) {
      event.stopPropagation(); // Evitar que se propague al clic de la orden
    }
    
    const key = `${orderId}_${sparePartId}`;
    if (this.expandedSpareParts.has(key)) {
      this.expandedSpareParts.delete(key);
    } else {
      this.expandedSpareParts.add(key);
    }
  }

  isSparePartExpanded(orderId: number, sparePartId: number): boolean {
    const key = `${orderId}_${sparePartId}`;
    return this.expandedSpareParts.has(key);
  }

  // Limpiar expansiones de repuestos cuando se contrae una orden
  private clearSparePartExpansions(orderId: number) {
    const keysToDelete = Array.from(this.expandedSpareParts).filter(key => 
      key.startsWith(`${orderId}_`)
    );
    keysToDelete.forEach(key => this.expandedSpareParts.delete(key));
  }

  // Obtener clase CSS para estados de repuestos más específica
  getDetailedSparePartStatusClass(status: string): string {
    const classes = {
      'CREADO': 'bg-blue-100 text-blue-800',
      'ATRASADO': 'bg-red-100 text-red-800',
      'DEVOLUCION-PROVEEDOR': 'bg-yellow-100 text-yellow-800',
      'STOCK-ENTREGA-EN-LOCAL': 'bg-green-100 text-green-800',
      'STOCK-ENTREGA-AL-CLIENTE': 'bg-green-100 text-green-800',
      'STOCK-OTRAS-EXPORTACIONES': 'bg-purple-100 text-purple-800',
      'STOCK-EXPORTACION-ACTUAL': 'bg-indigo-100 text-indigo-800',
      'EMPAQUETADO-EXPORTACION-ACTUAL': 'bg-indigo-100 text-indigo-800',
      'EXPORTADO': 'bg-gray-100 text-gray-800',
      'ENTREGADO-EN-LOCAL': 'bg-green-100 text-green-800',
      'ENVIADO-AL-TALLER': 'bg-orange-100 text-orange-800'
    };
    return classes[status as keyof typeof classes] || 'bg-gray-100 text-gray-800';
  }

  // Formatear peso con unidades
  formatWeight(weight: number): string {
    if (!weight) return 'N/A';
    return weight >= 1000 ? `${(weight / 1000).toFixed(2)} kg` : `${weight} g`;
  }

  // Formatear booleanos
  formatBoolean(value: boolean): string {
    return value ? 'Sí' : 'No';
  }
} 